import { all, fork, put, takeLatest, call, select, delay } from "redux-saga/effects"
import * as actionTypes from "appRedux/actions/roomPlan/roomplanResourceActionType"
import * as actionCreators from "appRedux/actions/roomPlan/roomplanResource"
import { userSignOut } from "appRedux/actions/Auth"
import { API_URL_V1, API_PMS_HEADERS } from "constants/ApiSetting"
import { getItems } from "util/localStorage"
import { ApiResponse } from "constants/ActionTypes"
import { AppState } from "ReducerState"
import {
  buildRoomPlanQueryAuthScope,
  fetchRoomPlanQuery,
  isRoomPlanForceRefresh,
  stripRoomPlanInternalQueryFlags,
} from "services/api/roomPlanQuery"

const DEFAULT_RESOURCE_PAGE_SIZE = 1000;

const ROOM_STATUS_FILTERS = [
  ["statusVAC", "VAC"],
  ["statusOCC", "OCC"],
  ["statusOOO", "OOO"],
  ["statusOOS", "OOS"],
  ["statusOOI", "OOI"],
] as const;

const MAID_STATUS_FILTERS = [
  ["maidDirty", "DI"],
  ["maidIn", "MI"],
  ["maidClean", "CL"],
  ["maidInspected", "IS"],
  ["maidPickup", "PU"],
] as const;

const RESOURCE_ID_FILTERS = [
  "roomtype_id",
  "bedtype_id",
  "roomgroup_id",
  "building_id",
  "location_id",
  "exposure_id",
] as const;

const RESOURCE_FILTER_KEYS = [
  "roomNumberSearch",
  ...RESOURCE_ID_FILTERS,
  ...ROOM_STATUS_FILTERS.map(([key]) => key),
  ...MAID_STATUS_FILTERS.map(([key]) => key),
];

const hasValue = (value: any) => value !== undefined && value !== null && value !== "";

const normalizeResourceFilters = (payload: any = {}) => {
  const normalized: any = {
    roomNumberSearch: hasValue(payload.roomNumberSearch)
      ? String(payload.roomNumberSearch).trim()
      : undefined,
  };

  RESOURCE_ID_FILTERS.forEach((key) => {
    normalized[key] = hasValue(payload[key]) ? payload[key] : undefined;
  });

  ROOM_STATUS_FILTERS.forEach(([key]) => {
    normalized[key] = Boolean(payload[key]);
  });

  MAID_STATUS_FILTERS.forEach(([key]) => {
    normalized[key] = Boolean(payload[key]);
  });

  return normalized;
};

const getActiveStatuses = (filters: any, entries: readonly (readonly [string, string])[]) =>
  entries
    .filter(([key]) => Boolean(filters[key]))
    .map(([, status]) => status);

const appendResourceFilterParams = (qs: URLSearchParams, payload: any = {}) => {
  const filters = normalizeResourceFilters(payload);

  if (hasValue(payload.start_date)) qs.set("start_date", String(payload.start_date));
  if (hasValue(payload.end_date)) qs.set("end_date", String(payload.end_date));
  if (filters.roomNumberSearch) qs.set("roomNumberSearch", filters.roomNumberSearch);

  RESOURCE_ID_FILTERS.forEach((key) => {
    if (hasValue(filters[key])) qs.set(key, String(filters[key]));
  });

  const roomStatuses = getActiveStatuses(filters, ROOM_STATUS_FILTERS);
  const maidStatuses = getActiveStatuses(filters, MAID_STATUS_FILTERS);
  if (roomStatuses.length > 0) qs.set("statuses", roomStatuses.join(","));
  if (maidStatuses.length > 0) qs.set("hk_statuses", maidStatuses.join(","));
};

const hasResourceFilterPayload = (payload: any = {}) =>
  RESOURCE_FILTER_KEYS.some((key) => Object.prototype.hasOwnProperty.call(payload, key));

const hasActiveResourceFilters = (payload: any = {}) => {
  const filters = normalizeResourceFilters(payload);
  if (filters.roomNumberSearch) return true;
  if (RESOURCE_ID_FILTERS.some((key) => hasValue(filters[key]))) return true;
  if (ROOM_STATUS_FILTERS.some(([key]) => Boolean(filters[key]))) return true;
  if (MAID_STATUS_FILTERS.some(([key]) => Boolean(filters[key]))) return true;
  return false;
};

const sameFilterValue = (sourceValue: any, filterValue: any) => {
  if (!hasValue(filterValue)) return true;
  return String(sourceValue ?? "") === String(filterValue);
};

const filterResourceTree = (rawDataSource: any[] = [], payload: any = {}) => {
  const filters = normalizeResourceFilters(payload);
  const roomStatusSet = new Set(getActiveStatuses(filters, ROOM_STATUS_FILTERS));
  const maidStatusSet = new Set(getActiveStatuses(filters, MAID_STATUS_FILTERS));
  const roomSearch = String(filters.roomNumberSearch || "").toLowerCase();

  return rawDataSource
    .map((obj: any) => {
      const children = Array.isArray(obj?.children) ? obj.children : [];
      const childFiltered = children.filter((room: any) => {
        const ext = room?.extendedProps || {};

        if (roomStatusSet.size > 0 && !roomStatusSet.has(String(ext.status || ""))) return false;
        if (maidStatusSet.size > 0 && !maidStatusSet.has(String(ext.hk_status || ""))) return false;

        if (!sameFilterValue(ext.roomgroup_id, filters.roomgroup_id)) return false;
        if (!sameFilterValue(ext.roomtype_id, filters.roomtype_id)) return false;
        if (!sameFilterValue(ext.bedtype_id, filters.bedtype_id)) return false;
        if (!sameFilterValue(ext.building_id, filters.building_id)) return false;
        if (!sameFilterValue(ext.location_id, filters.location_id)) return false;
        if (!sameFilterValue(ext.exposure_id, filters.exposure_id)) return false;

        if (roomSearch) {
          const roomNumber = String(ext.room_number || room?.title || "").toLowerCase();
          if (!roomNumber.includes(roomSearch)) return false;
        }

        return true;
      });

      return childFiltered.length > 0 ? { ...obj, children: childFiltered } : undefined;
    })
    .filter((resource: any) => resource !== undefined);
};

const annotateResourceOrder = (resources: any[]): any[] => {
  if (!Array.isArray(resources)) return [];
  return resources.map((resource, index) => {
    const children = Array.isArray(resource?.children)
      ? resource.children.map((child: any, childIndex: number) => ({
          ...child,
          extendedProps: {
            ...(child?.extendedProps || {}),
            __apiIndex: childIndex,
          },
        }))
      : [];

    return {
      ...resource,
      extendedProps: {
        ...(resource?.extendedProps || {}),
        __apiIndex: index,
      },
      children,
    };
  });
};

function* roomplanResourceRequestProcess(
  { payload }: actionTypes.RoomPlanResourceRequest
): Generator<any, void, any> {

  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const authUser = yield select((state: AppState) => state.auth.authUser)
  const authScope = buildRoomPlanQueryAuthScope(authUser)

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const requestPayload = stripRoomPlanInternalQueryFlags(payload || {});
  const forceRefresh = isRoomPlanForceRefresh(payload);
  const {
    level = "parent",
    parentId,
    page = 1,
    pageSize = DEFAULT_RESOURCE_PAGE_SIZE,
    slim = true,
    fields = [
      "id",
      "title",
      "extendedProps.roomtype",
      "extendedProps.roomtype_code",
      "extendedProps.order_seq",
      "extendedProps.status",
      "extendedProps.hk_status",
      "extendedProps.room_number",
      "extendedProps.roomgroup_id",
      "extendedProps.roomtype_id",
      "extendedProps.bedtype_id",
      "extendedProps.building_id",
      "extendedProps.location_id",
      "extendedProps.exposure_id",
      "hasChildren",
    ]
  } = requestPayload || {};
  const { filtersRoomplan } = yield select((state: AppState) => state.roomplanResouce)
  const filterPayload = hasResourceFilterPayload(requestPayload)
    ? requestPayload
    : filtersRoomplan || {};

  const qs = new URLSearchParams();
  qs.set("format","2");
  qs.set("level", level);
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  if (parentId) qs.set("parentId", parentId);
  if (slim) qs.set("slim","true");
  qs.set("fields", fields.join(","));
  appendResourceFilterParams(qs, filterPayload);

  const url = `${API_URL_V1}pms/inventory/roomPlan/resource/?${qs.toString()}`;
  try {
    const response: ApiResponse = yield call(
      fetchRoomPlanQuery,
      "resource",
      url,
      API_PMS_HEADERS,
      forceRefresh,
      authScope
    )
    if (response.status === 200) {
      const requestFilters = normalizeResourceFilters(filterPayload);
      const hasActiveFilters = hasActiveResourceFilters(requestFilters);
      const resources = annotateResourceOrder(response.data || []);
      const nextResources = hasActiveFilters ? filterResourceTree(resources, requestFilters) : resources;
      if (!hasActiveFilters) {
        yield put(actionCreators.roomplanResourceRawDataSource(resources));
      }
      yield put(actionCreators.roomplanResourceSetFilter(requestFilters));
      yield put(actionCreators.roomplanResourceDatasouceSuccess(nextResources));

    } else {
      /* Show error message and signout */
      const { message, data } = response.data
      const processStatus = { status: false, action: "roomplanGetResource", message }
      yield put(actionCreators.roomplanResourceProcessSuccess(processStatus))
      yield delay(100)
      yield put(actionCreators.roomplanResourceProcessReset())
      yield delay(100)
      if (response.status === 403) {
        //if data is not null force signout 
        if (data)
          yield put(userSignOut())
      }

    }
  } catch (error) {
    const processStatus = { status: false, action: "roomplanGetResource", message: error }
    yield put(actionCreators.roomplanResourceProcessSuccess(processStatus))
    yield delay(100)
    yield put(actionCreators.roomplanResourceProcessReset())
    yield delay(100)
  }
}
function* roomplanResouceFilterRawDataProcess({ payload }: actionTypes.RoomPlanResourceFilterRawData) {
  const filters = normalizeResourceFilters(payload || {});
  const { rawDataSource } = yield select((state: AppState) => state.roomplanResouce)
  const dataReform = filterResourceTree(rawDataSource, filters);
  yield put(actionCreators.roomplanResourceSetFilter(filters))
  yield put(actionCreators.roomplanResourceDatasouceSuccess(dataReform))
}

function* roomplanResourceRequest() {
  yield takeLatest(actionTypes.ROOMPLAN_RESOURCE_REQUEST, roomplanResourceRequestProcess)
}
function* roomplanResourceFilterRawData() {
  yield takeLatest(actionTypes.ROOMPLAN_RESOURCE_FILTER_RAWDATA, roomplanResouceFilterRawDataProcess)
}

export default function* rootSaga() {
  yield all([fork(roomplanResourceRequest),
  fork(roomplanResourceFilterRawData)
  ])
}
